<template>
  <div class="w-full">
    <Transition name="fade-slide">
      <div v-if="showContent" class="mb-10 text-center fade-slide-2">
        <h2 class="text-[32px] font-normal text-dark mb-2">Forgot Password</h2>
        <p class="text-gray-500">Enter your email to receive a password reset link</p>
      </div>
    </Transition>

    <!-- Form -->
    <Transition name="fade-slide">
      <form v-if="showContent" @submit.prevent="handleSubmit" class="space-y-6 fade-slide-3">
        <div>
          <label class="text-sm font-medium text-dark mb-2 block">Email address</label>
          <div class="relative">
            <Icon name="ph:envelope" class="absolute left-4 top-1/2 -translate-y-1/2" :class="[form.email ? (errors.email ? 'text-red-500' : 'text-green-500') : 'text-gray-500']" />
            <input
              type="email"
              v-model="form.email"
              placeholder="name@company.com"
              class="w-full pl-11 pr-4 py-[14px] rounded-xl border bg-white text-dark placeholder:text-gray-500 focus:outline-none focus:ring-2"
              :class="[form.email ? (errors.email ? 'border-red-500 focus:ring-red-500/20 focus:border-red-500' : 'border-green-500 focus:ring-green-500/20 focus:border-green-500') : 'border-[#E8ECEF] focus:ring-primary/20 focus:border-primary']"
              @input="validateEmail"
              required />
            <Icon v-if="form.email && !errors.email" name="ph:check-circle-fill" class="absolute right-4 top-1/2 -translate-y-1/2 text-green-500 w-5 h-5" />
          </div>
          <p v-if="errors.email" class="mt-1 text-sm text-red-500">{{ errors.email }}</p>
          <p v-else-if="form.email && !errors.email" class="mt-1 text-sm text-green-500">Email is valid</p>
        </div>

        <button
          type="submit"
          class="w-full bg-dark text-white rounded-xl py-[14px] font-medium hover:bg-dark/90 transition-colors focus:outline-none focus:ring-2 focus:ring-dark/20 disabled:opacity-70 disabled:cursor-not-allowed"
          :disabled="isLoading || !isFormValid">
          <span class="flex items-center justify-center">
            <Icon v-if="isLoading" name="svg-spinners:180-ring" class="mr-2 h-5 w-5" />
            {{ isLoading ? "Sending..." : "Send Reset Link" }}
          </span>
        </button>

        <!-- Success message -->
        <div v-if="successMessage" class="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl" role="alert">
          <div class="flex">
            <div class="flex-shrink-0">
              <Icon name="ph:check-circle" class="h-5 w-5 text-green-400" />
            </div>
            <div class="ml-3">
              <p class="text-sm">{{ successMessage }}</p>
            </div>
          </div>
        </div>

        <!-- Error message -->
        <div v-if="errorMessage" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl" role="alert">
          <div class="flex">
            <div class="flex-shrink-0">
              <Icon name="ph:warning-circle" class="h-5 w-5 text-red-400" />
            </div>
            <div class="ml-3">
              <p class="text-sm">{{ errorMessage }}</p>
            </div>
          </div>
        </div>

        <!-- Back to Login Link -->
        <div class="text-center space-y-4">
          <p class="text-gray-500 text-sm">
            Remember your password?
            <NuxtLink to="/auth/login" class="text-dark font-medium hover:text-dark/80 transition-colors ml-1 underline">Back to login</NuxtLink>
          </p>
          <div class="flex items-center justify-center">
            <span class="px-2 text-gray-500 text-xs text-center">
              By continuing, you agree to our <NuxtLink to="/terms" class="text-dark font-medium hover:text-dark/80 transition-colors ml-1 underline">Terms of Service</NuxtLink> and
              <NuxtLink to="/privacy" class="text-dark font-medium hover:text-dark/80 transition-colors ml-1 underline">Privacy Policy</NuxtLink>
            </span>
          </div>
        </div>
      </form>
    </Transition>
  </div>
</template>

<script setup>
definePageMeta({
  layout: "auth",
  middleware: "guest",
})

const router = useRouter()
const { $toast } = useNuxtApp()

// Form state
const form = reactive({
  email: "",
})

// Validation state
const errors = reactive({
  email: "",
})

// UI state
const isLoading = ref(false)
const errorMessage = ref("")
const successMessage = ref("")
const showContent = ref(false)

// Computed property to check if form is valid
const isFormValid = computed(() => {
  return form.email && !errors.email
})

// Validation function
const validateEmail = () => {
  if (!form.email) {
    errors.email = "Email address is required"
    return
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(form.email)) {
    errors.email = "Please enter a valid email address"
    return
  }

  errors.email = ""
}

const authStore = useAuthStore()

// Handle form submission
const handleSubmit = async () => {
  // Reset messages
  errorMessage.value = ""
  successMessage.value = ""

  // Validate email
  validateEmail()
  if (errors.email) return

  // Show loading state
  isLoading.value = true

  try {
    const response = await authStore.forgotPassword(form.email)
    if (response.success) {
      successMessage.value = `Password reset instructions have been sent to ${form.email}. Please check your inbox.`
      form.email = ""
      $toast.success(response.message)
    }
  } catch (error) {
    console.error("Forgot password error:", error)
    errorMessage.value = "Failed to send reset link. Please try again later."
    $toast.error(error.response?.data?.message || "Failed to send reset link")
  } finally {
    isLoading.value = false
  }
}

// Show content with animation on mount
onMounted(() => {
  showContent.value = true
})
</script>

<style scoped>
.fade-slide-enter-active {
  transition: all 0.5s ease-out;
}

.fade-slide-leave-active {
  transition: all 0.3s ease-in;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.fade-slide-2 {
  animation: fadeSlideIn 0.5s ease-out 0.2s both;
}

.fade-slide-3 {
  animation: fadeSlideIn 0.5s ease-out 0.4s both;
}

@keyframes fadeSlideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
